// BUILD-042 — HR Administration > Employees (Owner decision 2026-10-24, "Overall HR Administration composition" and the four-image flow clarification).
//
// The landing page of HR Administration. Employees come first, above the fold, with three route-addressable tabs:
//   * Employees       - every person with an HR record (search, "Show inactive", "Show archived", per-person badges). A person whose initial HR setup is
//                       in progress shows an "In progress" badge and a "Continue setup" action.
//   * Awaiting setup  - the people HR still has to set up: nobody-with-no-HR-record (existing eligibility rules) PLUS people whose guided setup is in
//                       progress, each person counted ONCE. "Start HR setup" asks for a hire date inline (the existing createHrRecord call, unchanged
//                       record class / eligibility handling / error messages) and then opens THAT person's guided setup; "Continue setup" resumes it.
//                       Finishing setup removes the person from this queue but never from Employees.
//   * Open work       - the existing company-wide pending-work view (PendingWorkPanel: filters, exact totals, destinations). ONE amber count on the tab,
//                       drawn only when something is open; a failed check draws "!" (never a zero).
// Tab selection is in the URL (?tab=awaiting | ?tab=open; absent = Employees), so each view can be linked, refreshed and reached with Back.
//
// The roster has four distinct truths that must never be mixed up: still loading (skeleton), loaded and genuinely empty, the request failed
// (unavailable, with Retry) and denied (no permission). A failed roster call is NEVER rendered as an empty list.
//
// Nothing here changes who may do what: creation, eligibility, the no-self-finalization rule and every refusal message are the existing ones; the
// server re-authorizes every call.
import { useCallback, useEffect, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listHrAdminEmployees, createHrRecord, invalidateHrAdminEmployeesCache, createFictionalFixtureEmployeeForUser, getHrSetup,
  type HrAdminEmployeeListEntry, type HrSetupStep,
} from '../../api/client';
import { useAdminAttention } from './attentionStore';
import { badgeFor, employeeBadge, pendingBadgeLabel } from './workspace/attentionModel';
import { classifyLoadError } from './workspace/loadState';
import { CREATE_FICTIONAL_PERSON_AVAILABLE } from './hrFeatures';
import { SelfSetupCard } from './SelfSetupCard';
import { useHrAccess } from './HrAccessContext';
import { PendingWorkPanel } from './PendingWorkPanel';
import { Avatar } from './workspace/Avatar';
import { CardTitle } from './workspace/SectionHeading';
import { formatLocalDate } from './hrFormat';

type TabKey = 'employees' | 'awaiting' | 'open';
const TAB_ORDER: TabKey[] = ['employees', 'awaiting', 'open'];

type RosterState =
  | { kind: 'loading' }
  | { kind: 'ready'; employees: HrAdminEmployeeListEntry[] }
  | { kind: 'denied' }
  | { kind: 'unavailable'; message: string };

const STEP_LABEL: Record<HrSetupStep, string> = {
  details: 'Employee details', benefits: 'Benefits', checklist: 'Onboarding checklist', documents: 'Documents', equipment: 'Equipment', review: 'Review',
};

export const setupPathFor = (employeeId: string) => `/hr/admin/employees/${employeeId}/setup`;
const recordPathFor = (employeeId: string) => `/hr/admin/employees/${employeeId}`;

const pill = 'inline-flex items-center max-w-full text-xs font-medium rounded-full px-2 py-0.5 border break-words';
const PILL_NEUTRAL = `${pill} text-gray-700 bg-gray-100 border-gray-200`;
const PILL_AMBER = `${pill} text-amber-900 bg-amber-50 border-amber-300`;
const PILL_BLUE = `${pill} text-[#176A9B] bg-[#EDF5FC] border-[#BBD9EE]`;

function CountPill({ children, tone, testId, label }: { children: string; tone: 'grey' | 'amber'; testId?: string; label?: string }) {
  return (
    <span
      data-testid={testId}
      data-tone={tone}
      title={label}
      aria-label={label}
      className={`ml-1.5 inline-flex items-center justify-center min-w-[1.25rem] text-[11px] font-semibold leading-none rounded-full px-1.5 py-1 ${tone === 'amber' ? 'bg-amber-100 text-amber-900 border border-amber-300' : 'bg-gray-100 text-gray-600'}`}
    >
      {children}
    </span>
  );
}

export function EmployeesPanel({ viewerEmployeeId }: { viewerEmployeeId: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab: TabKey = rawTab === 'awaiting' || rawTab === 'open' ? rawTab : 'employees';
  const adminAttention = useAdminAttention();
  const hrAccess = useHrAccess();
  const hireDateId = useId();

  function chooseTab(next: TabKey) {
    setSearchParams(next === 'employees' ? {} : { tab: next });
  }
  function onTabKey(e: KeyboardEvent) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = TAB_ORDER.indexOf(tab);
    const next = TAB_ORDER[(i + (e.key === 'ArrowRight' ? 1 : TAB_ORDER.length - 1)) % TAB_ORDER.length];
    chooseTab(next);
    requestAnimationFrame(() => document.getElementById(`employees-tab-${next}`)?.focus());
  }

  // ── the roster, with four distinct states ───────────────────────────────────────────────────────────────────────────────────
  const [roster, setRoster] = useState<RosterState>({ kind: 'loading' });
  const loadRoster = useCallback(() => {
    let cancelled = false;
    setRoster({ kind: 'loading' });
    listHrAdminEmployees()
      .then((d) => { if (!cancelled) setRoster({ kind: 'ready', employees: d.employees }); })
      .catch((e) => {
        if (cancelled) return;
        const v = classifyLoadError(e, { gatedList: true });
        setRoster(v.kind === 'denied' ? { kind: 'denied' } : { kind: 'unavailable', message: v.kind === 'unavailable' ? v.message : 'The request failed.' });
      });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => loadRoster(), [loadRoster]);
  /** Re-read after one of our own writes, without flashing the skeleton. A failed quiet re-read keeps what is already on screen. */
  const refreshQuietly = () => listHrAdminEmployees().then((d) => setRoster({ kind: 'ready', employees: d.employees })).catch(() => { /* keep the list already shown */ });
  const retry = () => { invalidateHrAdminEmployeesCache(); loadRoster(); };

  // ── filters ─────────────────────────────────────────────────────────────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  // BUILD-042 Owner-led onboarding (2026-10-07): default view hides disabled/inactive platform access - a VISIBILITY filter only, fully recoverable.
  const [showInactive, setShowInactive] = useState(false);
  // Oct 22 lifecycle: archived people are hidden by default. Visibility only; separate from "Show inactive", which is about account access.
  const [showArchived, setShowArchived] = useState(false);

  // ── your own HR record (the approved path for your own record; nobody sets up their own record from this list) ──────────────
  const [selfSetupSignal, setSelfSetupSignal] = useState(0);
  const [selfSetupNote, setSelfSetupNote] = useState<string | null>(null);
  const selfSetupAvailable = hrAccess.state.kind === 'ready' && hrAccess.state.me.selfSetupAvailable === true && !hrAccess.state.me.hasHrRecord;
  function requestSelfSetup() {
    if (selfSetupAvailable) { setSelfSetupNote(null); setSelfSetupSignal((n) => n + 1); return; }
    setSelfSetupNote('Setting up your own HR record isn’t available to you right now. Ask another HR administrator to set it up.');
  }

  // ── Start HR setup: the inline hire-date block on one row (only ONE row open at a time) ─────────────────────────────────────
  const [setupRowId, setSetupRowId] = useState<string | null>(null);
  const [hireDate, setHireDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);

  function openRowSetup(e: HrAdminEmployeeListEntry) { setSetupRowId(e.employeeId); setHireDate(''); setRowError(null); }
  function cancelRowSetup() { setSetupRowId(null); setHireDate(''); setRowError(null); }

  // Same createHrRecord call, same record class, same server eligibility check and same plain-language refusal as before. On success the
  // administrator goes STRAIGHT to this person's guided setup (not back to the roster). On an error the entered date is kept and the message
  // stays on the row.
  async function confirmRowSetup(e: HrAdminEmployeeListEntry) {
    if (!hireDate || creating) return;
    setCreating(true);
    setRowError(null);
    try {
      await createHrRecord(e.employeeId, { hireDate });
      setSetupRowId(null);
      setHireDate('');
      navigate(setupPathFor(e.employeeId));
    } catch (err: any) {
      // Cycle 12: another administrator created this person's record first. That is not a failure: refresh the roster, then open THAT person's unfinished setup
      // (or, when setup is already finished or the record predates guided setup, the record itself). Nothing was written by this request.
      if (err?.status === 409 && err?.body?.existing === true) {
        setSetupRowId(null);
        setHireDate('');
        setRowError(null);
        invalidateHrAdminEmployeesCache();
        await refreshQuietly();
        let inProgress = false;
        try { inProgress = (await getHrSetup(e.employeeId)).progress?.status === 'in_progress'; } catch { /* unreadable setup status: open the record */ }
        navigate(inProgress ? setupPathFor(e.employeeId) : recordPathFor(e.employeeId));
        return;
      }
      if (err?.status === 403 && /no-self-finalization/i.test(String(err?.message ?? ''))) {
        setRowError('You can’t set up your own record from this list. Use “Set up your own HR record” at the top of this page. Nothing was changed.');
        return;
      }
      const refused = err?.status === 403 && /not a controlled TEST-DISPOSABLE fixture|not permitted in this environment/i.test(String(err?.message ?? ''));
      setRowError(refused
        ? 'HR setup is not turned on yet for ordinary employees in this environment. Nothing was changed.'
        : (err?.message || 'Could not create the HR record.'));
    } finally {
      setCreating(false);
    }
  }

  // ── controlled-fixture "Create fictional person": present ONLY where it is functional (the opt-in build flag). Otherwise there is no control. ──
  const fixtureCreationAvailable = CREATE_FICTIONAL_PERSON_AVAILABLE;
  const [fixtureFormOpen, setFixtureFormOpen] = useState(false);
  const [fixtureUsername, setFixtureUsername] = useState('');
  const [fixtureNote, setFixtureNote] = useState('');
  const [fixtureCreating, setFixtureCreating] = useState(false);
  const [fixtureStatus, setFixtureStatus] = useState<string | null>(null);

  async function handleCreateFixture() {
    if (!fixtureCreationAvailable) return; // defense in depth
    if (!fixtureUsername.trim() || fixtureCreating) return;
    setFixtureCreating(true);
    setFixtureStatus(null);
    try {
      const result: any = await createFictionalFixtureEmployeeForUser(fixtureUsername.trim(), fixtureNote.trim() || undefined);
      if (!result?.employeeId) {
        setFixtureStatus('The fictional person could not be confirmed. Check the Awaiting setup list before trying again.');
        return;
      }
      invalidateHrAdminEmployeesCache();
      setFixtureUsername('');
      setFixtureNote('');
      setFixtureFormOpen(false);
      chooseTab('awaiting');
      setSetupRowId(result.employeeId);
      setHireDate('');
      setRowError(null);
      setFixtureStatus('Fictional test person created. Find them under Awaiting setup and choose Start HR setup.');
      await refreshQuietly();
    } catch (e: any) {
      const status = e?.status;
      setFixtureStatus(
        status === 409
          ? 'That login is already registered as an employee, so it cannot be used as a fictional test person. Add a new login in Settings > User Management and use that one. Nothing was changed.'
          : status === 404
          ? 'No login was found for that email. Add the login in Settings > User Management first. Nothing was changed.'
          : status === 403
          ? 'Only administrators can create a fictional test person.'
          : `Could not create the fictional person. Check the Awaiting setup list before trying again.${e?.message ? ` (${e.message})` : ''}`
      );
    } finally {
      setFixtureCreating(false);
    }
  }

  // ── derived lists. Counts and lists come from the SAME filtered set, so a count always matches what the tab shows. ─────────────
  const all = roster.kind === 'ready' ? roster.employees : [];
  const archivedHiddenCount = showArchived ? 0 : all.filter((e) => e.archived && (showInactive || e.globalStatus !== 'disabled')).length;
  const inView = all.filter((e) => (showInactive || e.globalStatus !== 'disabled') && (showArchived || !e.archived));
  const withRecord = inView.filter((e) => e.hasHrRecord);
  // Awaiting setup: no HR record yet, OR an initial setup that is in progress. One filter pass, so nobody is counted twice.
  const awaiting = inView.filter((e) => !e.hasHrRecord || e.setup?.status === 'in_progress');
  const q = search.trim().toLowerCase();
  const matches = (e: HrAdminEmployeeListEntry) => `${e.name ?? ''} ${e.email ?? ''} ${e.employeeId}`.toLowerCase().includes(q);
  const visible = (tab === 'awaiting' ? awaiting : withRecord).filter(matches);
  const openBadge = badgeFor(adminAttention, 'Open work');
  const counted = roster.kind === 'ready';

  function renderRow(e: HrAdminEmployeeListEntry) {
    const name = e.name ?? e.email ?? e.employeeId;
    const inProgress = e.hasHrRecord && e.setup?.status === 'in_progress';
    // Oct 20: the server says BEFORE the form whether setup can start for this person (older servers omit it => allowed).
    const setupBlocked = !e.hasHrRecord && e.setupEligibility?.canCreate === false;
    // Your OWN row can never be set up through this list (the server refuses it); it leads to the self-setup card.
    const isMe = !e.hasHrRecord && !!viewerEmployeeId && e.employeeId === viewerEmployeeId;
    const rowOpen = !isMe && setupRowId === e.employeeId && !e.hasHrRecord && !setupBlocked;
    const open = employeeBadge(adminAttention, e.employeeId);
    const ended = e.hasHrRecord && (e.employmentEnded || e.departure?.state === 'separated');
    const emailPrefix = e.name && e.email ? `${e.email} · ` : '';
    const second = inProgress
      ? `${emailPrefix}HR setup in progress · ${e.setup ? STEP_LABEL[e.setup.step] ?? e.setup.step : ''}`
      : e.hasHrRecord
        ? `${emailPrefix}${ended ? 'Employment ended' : 'Active employee'}`
        : `${emailPrefix}${isMe ? 'This is you · no HR record yet' : 'No HR record yet'}${setupBlocked ? ' · HR setup for ordinary employees is not turned on yet' : ''}`;
    const action = inProgress ? 'Continue setup →' : e.hasHrRecord ? 'Open record →' : isMe ? 'Set up my record →' : setupBlocked ? 'Not available yet' : rowOpen ? 'Close' : 'Start HR setup →';
    const onClick = () => {
      if (inProgress) navigate(setupPathFor(e.employeeId));
      else if (e.hasHrRecord) navigate(recordPathFor(e.employeeId));
      else if (isMe) requestSelfSetup();
      else if (rowOpen) cancelRowSetup();
      else openRowSetup(e);
    };
    return (
      <li key={e.employeeId} data-testid={`roster-row-${e.employeeId}`}>
        <button
          type="button"
          disabled={setupBlocked && !isMe}
          aria-expanded={!e.hasHrRecord && !setupBlocked && !isMe ? rowOpen : undefined}
          data-testid={isMe ? 'own-row-self-setup' : undefined}
          onClick={onClick}
          className="w-full flex items-start gap-3 py-3.5 text-left hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] disabled:cursor-not-allowed"
        >
          <Avatar name={name} colorKey={e.employeeId} sizeClass="w-9 h-9" textClass="text-xs" />
          <div className="flex-1 min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="min-w-0 flex-1 basis-[9rem]">
            {/* Oct 20 (Owner): no "Test person" badge on normal screens. */}
            <div className="text-sm font-semibold text-gray-900 break-words">{name}</div>
            <div className="text-xs text-gray-500 mt-0.5 break-words">{second}</div>
          </div>
          <div className="flex flex-wrap items-center justify-start gap-1.5 min-w-0 max-w-full">
            {inProgress && <span className={PILL_BLUE} data-testid={`row-setup-${e.employeeId}`}>In progress</span>}
            {/* Oct 22 lifecycle. "Departure scheduled" is shown only to a viewer who holds hr.lifecycle.manage (the server withholds the field otherwise); the
                person is still employed through the last working day. It is information, not an alarm. */}
            {e.departure?.state === 'departure_scheduled' && (
              <span className={PILL_NEUTRAL} data-testid={`row-departure-${e.employeeId}`}>Departure scheduled · last day {formatLocalDate(e.departure.lastWorkingDate)}</span>
            )}
            {e.archived && <span className={PILL_NEUTRAL} data-testid={`row-archived-${e.employeeId}`}>Archived</span>}
            {/* Oct 17/18: how many items are open for THIS person (waiting on them, waiting for HR review, open tasks) - the server's exact per-employee count. */}
            {open.show && open.tone === 'count' && <span className={PILL_AMBER} data-testid={`row-badge-${e.employeeId}`} aria-label={open.label}>{pendingBadgeLabel(open.count)}</span>}
          </div>
          <span className={`text-sm font-medium min-w-0 max-w-full break-words ${setupBlocked && !isMe ? 'text-gray-500' : 'text-[#176A9B]'}`}>{action}</span>
          </div>
        </button>
        {rowOpen && (
          <div data-testid="inline-hr-setup" className="pb-4 pl-12 pr-2">
            <p className="text-xs text-gray-600 mb-2">Enter a hire date to start this employee's HR setup. This does not create a login or give app access.</p>
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <label htmlFor={`${hireDateId}-${e.employeeId}`} className="text-xs text-gray-500">Hire date</label>
                <input
                  id={`${hireDateId}-${e.employeeId}`}
                  type="date"
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm"
                  value={hireDate}
                  onChange={(ev) => setHireDate(ev.target.value)}
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={() => confirmRowSetup(e)}
                disabled={creating || !hireDate}
                className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating…' : 'Confirm'}
              </button>
              <button type="button" onClick={cancelRowSetup} className="text-sm text-gray-500 px-4 py-1.5">Cancel</button>
            </div>
            {rowError && <div role="alert" data-testid="inline-hr-setup-error" className="text-sm text-red-700 mt-2">{rowError}</div>}
          </div>
        )}
      </li>
    );
  }

  const tabButton = (key: TabKey, label: string, count: ReactNode) => {
    const selected = tab === key;
    return (
      <button
        key={key}
        id={`employees-tab-${key}`}
        type="button"
        role="tab"
        aria-selected={selected}
        aria-controls="employees-tabpanel"
        tabIndex={selected ? 0 : -1}
        onClick={() => chooseTab(key)}
        data-testid={`employees-tab-${key}`}
        className={`text-sm pb-2 px-0.5 border-b-2 -mb-px whitespace-nowrap inline-flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE] rounded-t ${selected ? 'text-[#176A9B] border-[#4AABDE] font-semibold' : 'text-gray-500 border-transparent hover:text-gray-700'}`}
      >
        {label}{count}
      </button>
    );
  };

  return (
    <div data-testid="employees-panel" className="min-w-0">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">HR Administration</div>
          <h1 className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">Employees</h1>
          <p className="text-sm text-gray-500 mt-1">Manage employees and continue HR setup.</p>
        </div>
        {fixtureCreationAvailable && (
          <button
            type="button"
            onClick={() => setFixtureFormOpen((v) => !v)}
            className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-md"
          >
            + Create fictional person
          </button>
        )}
      </div>

      {selfSetupNote && <div role="status" data-testid="self-setup-unavailable" className="mb-4 text-sm text-gray-700 bg-white border border-gray-200 rounded-md px-3 py-2">{selfSetupNote}</div>}
      <SelfSetupCard openSignal={selfSetupSignal} onSaved={() => { invalidateHrAdminEmployeesCache(); refreshQuietly(); }} />

      {fixtureFormOpen && fixtureCreationAvailable && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5 mb-4">
          <CardTitle>Create a fictional person for onboarding testing</CardTitle>
          <p className="text-xs text-gray-600 mb-3">
            Enter the email of a NEW login you just added in Settings &gt; User Management (one that is not yet registered as an employee). This creates a fictional test person for HR testing. It cannot be used on an existing employee.
          </p>
          <div className="flex gap-2 flex-wrap items-end">
            <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Existing login email (e.g. larry.employee@yourdomain.test)" aria-label="Login email" value={fixtureUsername} onChange={(e) => setFixtureUsername(e.target.value)} />
            <input className="border border-gray-300 rounded px-3 py-1.5 text-sm" placeholder="Note (optional, e.g. 'Larry Employee')" aria-label="Note" value={fixtureNote} onChange={(e) => setFixtureNote(e.target.value)} />
            <button onClick={handleCreateFixture} disabled={fixtureCreating || !fixtureUsername.trim()} className="bg-[#4AABDE] text-white text-sm px-4 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed">
              {fixtureCreating ? 'Creating…' : 'Create'}
            </button>
            <button type="button" onClick={() => setFixtureFormOpen(false)} className="text-sm text-gray-500 px-4 py-1.5">Cancel</button>
          </div>
        </div>
      )}
      {fixtureCreationAvailable && fixtureStatus && (
        <div role="status" data-testid="fictional-person-status" className="text-sm text-gray-700 bg-white border border-gray-200 rounded-md px-3 py-2 mb-4">{fixtureStatus}</div>
      )}

      {/* The three tabs. Counts: grey for Employees and Awaiting setup (shown only once the roster has loaded); ONE amber count on Open work. */}
      <div role="tablist" aria-label="Employee views" onKeyDown={onTabKey} className="flex flex-wrap gap-x-6 gap-y-1 border-b border-gray-200 mb-4" data-testid="employees-tabs">
        {tabButton('employees', 'Employees', counted ? <CountPill tone="grey" testId="count-employees" label={`${withRecord.length} employees`}>{String(withRecord.length)}</CountPill> : null)}
        {tabButton('awaiting', 'Awaiting setup', counted ? <CountPill tone="grey" testId="count-awaiting" label={`${awaiting.length} awaiting setup`}>{String(awaiting.length)}</CountPill> : null)}
        {tabButton('open', 'Open work', openBadge.show ? <CountPill tone="amber" testId="count-open" label={openBadge.label}>{openBadge.text}</CountPill> : null)}
      </div>

      <div id="employees-tabpanel" role="tabpanel" aria-labelledby={`employees-tab-${tab}`}>
        {tab === 'open' ? (
          <PendingWorkPanel embedded />
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-3 sm:p-5 min-w-0">
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4">
              <input
                type="search"
                aria-label="Search employees"
                placeholder="Search by name, title or department"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full sm:flex-1 sm:w-auto min-w-0 sm:min-w-[12rem] border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
                  Show inactive (disabled access)
                </label>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer" data-testid="show-archived-label">
                  <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" />
                  Show archived
                </label>
              </div>
            </div>
            {counted && archivedHiddenCount > 0 && (
              <p className="text-[11px] text-gray-500 -mt-2 mb-3" data-testid="archived-hidden-note">
                {archivedHiddenCount} archived {archivedHiddenCount === 1 ? 'person is' : 'people are'} hidden from this list. Turn on “Show archived” to find them; their records, documents and open work are all still there.
              </p>
            )}

            {roster.kind === 'loading' ? (
              <div role="status" aria-live="polite" data-testid="roster-loading">
                <div className="text-sm text-gray-500 mb-2">Loading employees…</div>
                <ul aria-hidden="true" className="divide-y divide-gray-100 animate-pulse">
                  {[0, 1, 2, 3].map((i) => (
                    <li key={i} className="flex items-center gap-3 py-3.5">
                      <div className="w-9 h-9 rounded-full bg-gray-200 flex-shrink-0" />
                      <div className="flex-1 space-y-1.5"><div className="h-3 w-40 max-w-full rounded bg-gray-200" /><div className="h-2.5 w-28 max-w-full rounded bg-gray-100" /></div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : roster.kind === 'unavailable' ? (
              <div role="alert" data-testid="roster-unavailable" className="flex flex-wrap items-center justify-between gap-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                <span className="min-w-0 break-words">We could not load the employee list just now. This is not the same as having no employees.</span>
                <button type="button" onClick={retry} data-testid="roster-retry" className="text-sm font-medium text-[#176A9B] border border-[#4AABDE] rounded-md px-3 py-1 hover:bg-[#EDF5FC] flex-shrink-0">Retry</button>
              </div>
            ) : roster.kind === 'denied' ? (
              <div data-testid="roster-denied" className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">Your access does not include the employee list. Ask an administrator who can change access.</div>
            ) : visible.length === 0 ? (
              q
                ? <div className="text-sm text-gray-500 py-3" data-testid="roster-no-match">No one matches this search in this view.</div>
                : tab === 'awaiting'
                  ? <div className="text-sm text-gray-500 py-3" data-testid="roster-empty-awaiting">Nobody is waiting for HR setup.</div>
                  : roster.employees.length === 0
                    ? <div className="text-sm text-gray-500 py-3" data-testid="roster-empty">There are no employees yet.</div>
                    : <div className="text-sm text-gray-500 py-3" data-testid="roster-no-match">No employees match this view.</div>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid={tab === 'awaiting' ? 'awaiting-list' : 'employees-list'}>
                {visible.map(renderRow)}
              </ul>
            )}
          </div>
        )}
      </div>

      <details className="text-xs text-gray-500 mt-4">
        <summary className="text-gray-600 cursor-pointer">How this page works</summary>
        <div className="mt-2 max-w-lg space-y-1.5">
          <p>This list shows every employee this account can see, matched against whether they already have an HR record.</p>
          <p>Starting HR setup is the first step for a new employee. It records their hire date and then walks you through their details, benefits, checklist, documents and equipment. It does not change their login or app access, which are managed separately.</p>
          <p data-testid="lifecycle-note">Turning off someone's access only hides them here. Their HR record, documents and history are kept. Ending employment is recorded on the person's HR record, and no HR record is deleted from this screen.</p>
        </div>
      </details>
    </div>
  );
}
